import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import {
  formatCount,
  isNationalCodeChecksumValid,
  MOBILE_E164_PATTERN,
  normalizeCodeInput,
  normalizeMobileField,
  statusLabel,
  toLocalMobile,
} from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  Dialog,
  FieldErrorList,
  IconButton,
  Menu,
  MenuItem,
  SegmentedButtons,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type {
  AdminMerchant,
  MerchantKycVerification,
  MerchantStatusTransition,
  MerchantUpdate,
} from '../api.js';
import {
  ADMIN_FORM_OPTIONS,
  mutationSubmitDisabled,
  useSubmitBlocked,
} from '../components/form.js';
import { CategoryPicker, GeographyFields } from '../components/pickers.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { merchantCloseBlockedReason } from '../merchant-closure.js';
import { canUse } from '../permissions.js';
import { setupKeys } from '../queries.js';
import { reasonSchema } from '../validation.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * Merchant lifecycle on the merchant drawer (AC §18.4.3–§18.4.5, M1–M3; FTD §9.3): profile and KYC edit
 * (the KYC fields lock after activation, Q-S3-07), KYC verification, and the status transitions with
 * their requirement and settlement checks. Every result is the server's `AdminMerchant`; nothing is
 * applied before the `200`.
 */

export function useRefreshMerchant(merchantId: string) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.merchant(merchantId) }),
      queryClient.invalidateQueries({ queryKey: setupKeys.merchants }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
      queryClient.invalidateQueries({ queryKey: setupKeys.approvals }),
    ]);
  };
}

/** Codes after which the drawer reads the merchant again (it changed meanwhile). */
const RELOAD_CODES: ReadonlySet<string> = new Set([
  'RESOURCE_VERSION_CONFLICT',
  'STATE_CONFLICT',
  'MERCHANT_KYC_LOCKED',
]);

// ---------------------------------------------------------------------------------------------
// M1 updateMerchant

const optionalText = (max: number) => z.string().trim().max(max, `حداکثر ${max} کاراکتر مجاز است.`);

const editSchema = z
  .object({
    addressFa: optionalText(500).min(1, 'آدرس الزامی است.'),
    businessLicenseNumber: optionalText(64),
    cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
    contactMobileE164: z.string().transform(normalizeMobileField),
    displayNameFa: optionalText(200).min(1, 'نام فروشگاه الزامی است.'),
    legalEntityType: z.enum(['INDIVIDUAL', 'LEGAL_ENTITY']),
    legalNameFa: optionalText(300).min(1, 'نام حقوقی الزامی است.'),
    nationalId: z.string().transform(normalizeCodeInput),
    ownerNationalCode: z.string().transform(normalizeCodeInput),
    primaryCategoryId: z.string().min(1, 'دسته‌بندی را انتخاب کنید.'),
    provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
    reason: reasonSchema(),
    registrationNumber: optionalText(64),
  })
  .superRefine((value, context) => {
    const issue = (path: string, message: string) =>
      context.addIssue({ code: 'custom', message, path: [path] });
    if (value.contactMobileE164 !== '' && !MOBILE_E164_PATTERN.test(value.contactMobileE164)) {
      issue('contactMobileE164', 'شماره موبایل را مانند ۰۹۱۲۱۲۳۴۵۶۷ وارد کنید.');
    }
    if (
      value.ownerNationalCode !== '' &&
      (!/^\d{10}$/.test(value.ownerNationalCode) ||
        !isNationalCodeChecksumValid(value.ownerNationalCode))
    ) {
      issue('ownerNationalCode', 'کد ملی ۱۰ رقمی معتبر نیست.');
    }
    if (value.nationalId !== '' && !/^\d{11}$/.test(value.nationalId)) {
      issue('nationalId', 'شناسه ملی ۱۱ رقم است.');
    }
  });
type EditInput = z.input<typeof editSchema>;
type EditValues = z.output<typeof editSchema>;
const EDIT_FIELDS = [
  'displayNameFa',
  'legalNameFa',
  'legalEntityType',
  'nationalId',
  'ownerNationalCode',
  'registrationNumber',
  'businessLicenseNumber',
  'provinceId',
  'cityId',
  'primaryCategoryId',
  'contactMobileE164',
  'addressFa',
  'reason',
];

function initialEdit(merchant: AdminMerchant): EditInput {
  return {
    addressFa: merchant.addressFa,
    businessLicenseNumber: merchant.businessLicenseNumber ?? '',
    cityId: merchant.city.id,
    // Auditor and Marketing Admin get only the masked mobile (M-22); they cannot edit anyway.
    contactMobileE164:
      merchant.contactMobileE164 === undefined ? '' : toLocalMobile(merchant.contactMobileE164),
    displayNameFa: merchant.displayNameFa,
    legalEntityType: merchant.legalEntityType,
    legalNameFa: merchant.legalNameFa,
    nationalId: merchant.nationalId ?? '',
    // Only the masked national code is returned: a new value is typed in full, or left empty.
    ownerNationalCode: '',
    primaryCategoryId: merchant.primaryCategory.id,
    provinceId: merchant.province.id,
    reason: '',
    registrationNumber: merchant.registrationNumber ?? '',
  };
}

/**
 * The `MerchantUpdate` body: only the fields that changed (AC §18.4.3: at least one), and the KYC
 * fields only while the merchant is `PENDING_ACTIVATION`. Changing the legal entity type sends the
 * new type's identifier set and clears the other type's.
 */
export function toMerchantUpdate(
  merchant: AdminMerchant,
  values: EditValues,
): Omit<MerchantUpdate, 'expectedVersion' | 'reason'> {
  const body: Record<string, unknown> = {};
  const changed = (key: string, next: string, previous: string) => {
    if (next !== previous) body[key] = next;
  };
  changed('displayNameFa', values.displayNameFa, merchant.displayNameFa);
  changed('legalNameFa', values.legalNameFa, merchant.legalNameFa);
  changed('addressFa', values.addressFa, merchant.addressFa);
  changed('primaryCategoryId', values.primaryCategoryId, merchant.primaryCategory.id);
  if (values.provinceId !== merchant.province.id || values.cityId !== merchant.city.id) {
    body.provinceId = values.provinceId;
    body.cityId = values.cityId;
  }
  // The form holds the local spelling until it is submitted; compare in the stored E.164 form.
  const contactMobile = normalizeMobileField(values.contactMobileE164);
  if (contactMobile !== '' && contactMobile !== (merchant.contactMobileE164 ?? '')) {
    body.contactMobileE164 = contactMobile;
  }
  if (merchant.status === 'PENDING_ACTIVATION') {
    const individual = values.legalEntityType === 'INDIVIDUAL';
    if (values.legalEntityType !== merchant.legalEntityType) {
      body.legalEntityType = values.legalEntityType;
      body.nationalId = individual ? null : values.nationalId;
      body.registrationNumber = individual ? null : values.registrationNumber;
      body.businessLicenseNumber = individual ? values.businessLicenseNumber : null;
      body.ownerNationalCode = individual ? values.ownerNationalCode : null;
    } else if (individual) {
      changed(
        'businessLicenseNumber',
        values.businessLicenseNumber,
        merchant.businessLicenseNumber ?? '',
      );
      if (values.ownerNationalCode !== '') body.ownerNationalCode = values.ownerNationalCode;
    } else {
      changed('nationalId', values.nationalId, merchant.nationalId ?? '');
      changed('registrationNumber', values.registrationNumber, merchant.registrationNumber ?? '');
    }
  }
  return body as Omit<MerchantUpdate, 'expectedVersion' | 'reason'>;
}

const KYC_EVIDENCE_INDIVIDUAL = [
  { key: 'business-license', label: 'جواز کسب' },
  { key: 'owner-national-card', label: 'کارت ملی مالک' },
] as const;

const KYC_EVIDENCE_LEGAL = [
  { key: 'registration-cert', label: 'گواهی ثبت' },
  { key: 'official-gazette', label: 'روزنامهٔ رسمی' },
] as const;

function EditIdentityDialog({
  merchant,
  onOpenChange,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const kycEditable = merchant.status === 'PENDING_ACTIVATION';
  const form = useForm<EditInput, unknown, EditValues>({
    defaultValues: initialEdit(merchant),
    resolver: zodResolver(editSchema),
    ...ADMIN_FORM_OPTIONS,
  });
  const values = form.watch();
  const hasChanges =
    Object.keys(toMerchantUpdate(merchant, { ...initialEdit(merchant), ...values })).length > 0;
  const individual = values.legalEntityType === 'INDIVIDUAL';
  const command = useCommand<MerchantUpdate, AdminMerchant>(
    'merchant.update',
    (client, body) => client.patch(`/admin/merchants/${encodeURIComponent(merchant.id)}`, { body }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'مشخصات فروشگاه به‌روز شد' });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<AdminMerchant>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(applyFieldErrors(error, EDIT_FIELDS, form.setError));
    if (error.code === 'MERCHANT_NATIONAL_ID_ALREADY_REGISTERED') {
      form.setError(individual ? 'ownerNationalCode' : 'nationalId', {
        message: error.messageFa,
        type: 'server',
      });
    }
    if (error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError('cityId', { message: error.messageFa, type: 'server' });
    }
    if (RELOAD_CODES.has(error.code)) {
      await refresh();
    }
  }

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="ویرایش هویت"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          const merged = { ...initialEdit(merchant), ...values };
          const changes = toMerchantUpdate(merchant, merged);
          if (Object.keys(changes).length === 0) {
            return;
          }
          await handle(
            await command.submit({
              ...changes,
              expectedVersion: merchant.version,
              reason: values.reason.trim(),
            }),
          );
        })}
      >
        <div className="admin-grid-2">
          <TextField
            error={form.formState.errors.displayNameFa?.message}
            label="نام فروشگاه"
            {...form.register('displayNameFa')}
          />
          <TextField
            error={form.formState.errors.legalNameFa?.message}
            label="نام حقوقی"
            {...form.register('legalNameFa')}
          />
        </div>
        <fieldset className="admin-fieldset" disabled={!kycEditable}>
          <legend>شناسه‌های تأیید هویت</legend>
          <Controller
            control={form.control}
            name="legalEntityType"
            render={({ field }) => (
              <SegmentedButtons
                disabled={!kycEditable}
                label="نوع شخصیت"
                onChange={field.onChange}
                options={[
                  {
                    key: 'INDIVIDUAL',
                    label: statusLabel('merchantLegalEntityType', 'INDIVIDUAL'),
                  },
                  {
                    key: 'LEGAL_ENTITY',
                    label: statusLabel('merchantLegalEntityType', 'LEGAL_ENTITY'),
                  },
                ]}
                value={field.value}
              />
            )}
          />
          {individual ? (
            <div className="admin-grid-2">
              <TextField
                error={form.formState.errors.ownerNationalCode?.message}
                inputMode="numeric"
                label="کد ملی مالک"
                ltr
                placeholder={merchant.ownerNationalCodeMasked ?? undefined}
                {...form.register('ownerNationalCode')}
              />
              <TextField
                error={form.formState.errors.businessLicenseNumber?.message}
                label="شماره جواز کسب"
                ltr
                {...form.register('businessLicenseNumber')}
              />
            </div>
          ) : (
            <div className="admin-grid-2">
              <TextField
                error={form.formState.errors.nationalId?.message}
                inputMode="numeric"
                label="شناسه ملی"
                ltr
                placeholder={merchant.nationalId ?? undefined}
                {...form.register('nationalId')}
              />
              <TextField
                error={form.formState.errors.registrationNumber?.message}
                label="شماره ثبت"
                ltr
                {...form.register('registrationNumber')}
              />
            </div>
          )}
        </fieldset>
        <Controller
          control={form.control}
          name="primaryCategoryId"
          render={({ field }) => (
            <CategoryPicker
              error={form.formState.errors.primaryCategoryId?.message}
              id="merchant-edit-category"
              label="دسته‌بندی اصلی"
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل تغییر"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant.status." />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        <div className="admin-actions admin-actions--end">
          <Button
            disabled={mutationSubmitDisabled(
              offline,
              hasChanges,
              command.state.status === 'pending',
            )}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            ذخیره
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditContactDialog({
  merchant,
  onOpenChange,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<EditInput, unknown, EditValues>({
    defaultValues: initialEdit(merchant),
    resolver: zodResolver(editSchema),
    ...ADMIN_FORM_OPTIONS,
  });
  const values = form.watch();
  const hasChanges =
    Object.keys(toMerchantUpdate(merchant, { ...initialEdit(merchant), ...values })).length > 0;
  const command = useCommand<MerchantUpdate, AdminMerchant>(
    'merchant.update',
    (client, body) => client.patch(`/admin/merchants/${encodeURIComponent(merchant.id)}`, { body }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'مشخصات فروشگاه به‌روز شد' });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<AdminMerchant>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(applyFieldErrors(error, EDIT_FIELDS, form.setError));
    if (error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError('cityId', { message: error.messageFa, type: 'server' });
    }
    if (RELOAD_CODES.has(error.code)) {
      await refresh();
    }
  }

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="ویرایش تماس"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          const merged = { ...initialEdit(merchant), ...values };
          const changes = toMerchantUpdate(merchant, merged);
          if (Object.keys(changes).length === 0) {
            return;
          }
          await handle(
            await command.submit({
              ...changes,
              expectedVersion: merchant.version,
              reason: values.reason.trim(),
            }),
          );
        })}
      >
        <TextField
          autoComplete="off"
          error={form.formState.errors.contactMobileE164?.message}
          inputMode="tel"
          label="شماره موبایل تماس"
          ltr
          placeholder={merchant.contactMobileMasked ?? undefined}
          {...form.register('contactMobileE164')}
        />
        <Controller
          control={form.control}
          name="provinceId"
          render={({ field: province }) => (
            <Controller
              control={form.control}
              name="cityId"
              render={({ field: city }) => (
                <GeographyFields
                  cityError={form.formState.errors.cityId?.message}
                  cityId={city.value}
                  idPrefix="merchant-edit-contact"
                  onCityChange={city.onChange}
                  onProvinceChange={(value) => {
                    province.onChange(value);
                    city.onChange('');
                  }}
                  provinceError={form.formState.errors.provinceId?.message}
                  provinceId={province.value}
                />
              )}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.addressFa?.message}
          label="آدرس"
          {...form.register('addressFa')}
        />
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل تغییر"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant.status." />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        <div className="admin-actions admin-actions--end">
          <Button
            disabled={mutationSubmitDisabled(
              offline,
              hasChanges,
              command.state.status === 'pending',
            )}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            ذخیره
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// M2 verifyMerchantKyc

const kycSchema = z.object({
  evidenceKeys: z.array(z.string()).min(1, 'مدارک بررسی‌شده را انتخاب کنید.'),
  note: reasonSchema(false),
});

function kycEvidenceReference(merchant: AdminMerchant, keys: readonly string[]): string {
  const options =
    merchant.legalEntityType === 'INDIVIDUAL' ? KYC_EVIDENCE_INDIVIDUAL : KYC_EVIDENCE_LEGAL;
  const labels = options
    .filter((option) => keys.includes(option.key))
    .map((option) => option.label);
  return labels.join('، ');
}

function KycDialog({
  merchant,
  onOpenChange,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const evidenceOptions =
    merchant.legalEntityType === 'INDIVIDUAL' ? KYC_EVIDENCE_INDIVIDUAL : KYC_EVIDENCE_LEGAL;
  const form = useForm<{ evidenceKeys: string[]; note: string }>({
    defaultValues: { evidenceKeys: [], note: '' },
    resolver: zodResolver(kycSchema),
  });
  const command = useCommand<MerchantKycVerification, AdminMerchant>(
    'merchant.kyc.verify',
    (client, body, intent) =>
      client.post(`/admin/merchants/${encodeURIComponent(merchant.id)}/kyc-verification`, {
        body,
        intent,
      }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'هویت فروشگاه تأیید شد' });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<AdminMerchant>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, ['evidenceReference', 'note'], form.setError));
    if (outcome.error.fieldErrors.some((field) => field.field === 'evidenceReference')) {
      const evidenceMessage = outcome.error.fieldErrors.find(
        (field) => field.field === 'evidenceReference',
      )?.messageFa;
      if (evidenceMessage !== undefined) {
        form.setError('evidenceKeys', { message: evidenceMessage, type: 'server' });
      }
    }
    if (RELOAD_CODES.has(outcome.error.code)) await refresh();
  }

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="تأیید هویت فروشگاه"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          await handle(
            await command.submit({
              evidenceReference: kycEvidenceReference(merchant, values.evidenceKeys),
              expectedVersion: merchant.version,
              result: 'VERIFIED',
              ...(values.note.trim() === '' ? {} : { note: values.note.trim() }),
            }),
          );
        })}
      >
        <Controller
          control={form.control}
          name="evidenceKeys"
          render={({ field }) => (
            <div className="saay-field">
              <span className="saay-field__label">مدارک بررسی‌شده</span>
              <SegmentedButtons
                label="مدارک بررسی‌شده"
                mode="multiple"
                onChange={(next) => field.onChange([...next])}
                options={evidenceOptions.map((option) => ({
                  key: option.key,
                  label: option.label,
                }))}
                value={field.value}
              />
              {form.formState.errors.evidenceKeys?.message === undefined ? null : (
                <span className="saay-field__error" role="alert">
                  {form.formState.errors.evidenceKeys.message}
                </span>
              )}
            </div>
          )}
        />
        <TextArea
          error={form.formState.errors.note?.message}
          label="یادداشت (اختیاری)"
          maxLength={1000}
          {...form.register('note')}
        />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          تأیید هویت
        </Button>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// M3 transitionMerchantStatus

type Target = MerchantStatusTransition['targetStatus'];

const TRANSITION_COPY: Readonly<
  Record<'activate' | 'reactivate' | 'suspend' | 'close', { description: string; label: string }>
> = {
  activate: {
    description:
      'همه پیش‌نیازهای فعال‌سازی هنگام ثبت دوباره بررسی می‌شوند؛ اگر موردی ناقص باشد، فروشگاه فعال نمی‌شود.',
    label: 'فعال‌سازی فروشگاه',
  },
  close: {
    description:
      'بستن فروشگاه قابل برگشت نیست. همه کاربران فروشگاه غیرفعال و فروش‌های باز ناموفق می‌شوند. فقط وقتی تسویهٔ باقی‌مانده‌ای نباشد ممکن است.',
    label: 'بستن فروشگاه',
  },
  reactivate: {
    description: 'پیش‌نیازهای فعال‌سازی دوباره بررسی می‌شوند و پس از آن فروش جدید ممکن است.',
    label: 'فعال‌سازی دوباره',
  },
  suspend: {
    description:
      'با تعلیق، رزروهای فعال آزاد و درخواست‌های تأیید باز باطل می‌شوند؛ مرجوعی‌ها و تسویه‌ها ادامه دارند.',
    label: 'تعلیق فروشگاه',
  },
};

/** The recovery line of a refused transition, by error code only (AC §18.4.5). */
function transitionGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'MERCHANT_ACTIVATION_REQUIREMENTS_UNMET': {
      const missing = Array.isArray(details.missing)
        ? details.missing.filter((item): item is string => typeof item === 'string')
        : [];
      return missing.length === 0 ? undefined : (
        <>
          پیش‌نیازهای ناقص:
          <ul className="admin-plain-list">
            {missing.map((item) => (
              <li key={item}>{statusLabel('activationRequirement', item)}</li>
            ))}
          </ul>
        </>
      );
    }
    case 'PAYOUT_ACCOUNT_UNVERIFIED':
      return 'تنها پیش‌نیاز باقی‌مانده، تأیید حساب تسویه است؛ آن را در بخش «حساب تسویه» تأیید کنید.';
    case 'MERCHANT_CLOSURE_PENDING_SETTLEMENTS':
      return typeof details.pendingSettlementCount === 'number'
        ? `${formatCount(details.pendingSettlementCount)} تسویهٔ باقی‌مانده دارد؛ پس از پرداخت آن‌ها فروشگاه بسته می‌شود.`
        : undefined;
    case 'STATE_CONFLICT':
      return typeof details.currentStatus === 'string'
        ? `وضعیت فعلی: ${statusLabel('merchantStatus', details.currentStatus)}. اطلاعات به‌روز شد.`
        : 'اطلاعات به‌روز شد.';
    case 'RESOURCE_VERSION_CONFLICT':
      return 'فروشگاه در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد.';
    default:
      return undefined;
  }
}

type Transition = { readonly key: keyof typeof TRANSITION_COPY; readonly target: Target };

/** The transitions the contract allows from a status (AC §18.4.5); the server re-checks each. */
export function transitionsFrom(status: AdminMerchant['status']): readonly Transition[] {
  switch (status) {
    case 'PENDING_ACTIVATION':
      return [
        { key: 'activate', target: 'ACTIVE' },
        { key: 'close', target: 'CLOSED' },
      ];
    case 'ACTIVE':
      return [
        { key: 'suspend', target: 'SUSPENDED' },
        { key: 'close', target: 'CLOSED' },
      ];
    case 'SUSPENDED':
      return [
        { key: 'reactivate', target: 'ACTIVE' },
        { key: 'close', target: 'CLOSED' },
      ];
    default:
      return [];
  }
}

function TransitionDialog({
  merchant,
  onOpenChange,
  transition,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly transition: Transition;
}) {
  const toast = useToast();
  const refresh = useRefreshMerchant(merchant.id);
  const copy = TRANSITION_COPY[transition.key];
  return (
    <ReasonDialog<AdminMerchant>
      description={copy.description}
      eyebrow={merchant.displayNameFa}
      guidance={transitionGuidance}
      name={`merchant.status.${transition.target}`}
      onDone={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: `وضعیت فروشگاه: ${statusLabel('merchantStatus', result.data.status)}`,
        });
        onOpenChange(false);
      }}
      onError={async (error) => {
        if (isApiError(error) && (RELOAD_CODES.has(error.code) || error.status === 422)) {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      reasonLabel="دلیل تغییر وضعیت"
      send={(client, reason, intent) =>
        client.post(`/admin/merchants/${encodeURIComponent(merchant.id)}/status-transitions`, {
          body: {
            expectedVersion: merchant.version,
            reason,
            targetStatus: transition.target,
          } satisfies MerchantStatusTransition,
          intent,
        })
      }
      submitLabel={copy.label}
      title={copy.label}
      variant={transition.target === 'ACTIVE' ? 'primary' : 'danger'}
    >
      {transition.target === 'ACTIVE' && !merchant.activationReadiness.ready ? (
        <Banner tone="warning">
          {`پیش‌نیازهای ناقص: ${merchant.activationReadiness.missing.map((item) => statusLabel('activationRequirement', item)).join('، ')}`}
        </Banner>
      ) : null}
    </ReasonDialog>
  );
}

export type LifecycleOpen =
  | { readonly kind: 'edit-contact' }
  | { readonly kind: 'edit-identity' }
  | { readonly kind: 'kyc' }
  | { readonly kind: 'transition'; readonly transition: Transition };

/** Whether {@link LifecycleActions} offers anything (the header then has no action group). */
export function hasLifecycleActions(merchant: AdminMerchant, self: AdminSelf): boolean {
  const closed = merchant.status === 'CLOSED';
  return (
    (!closed && canUse(self, 'updateMerchant')) ||
    (merchant.status === 'PENDING_ACTIVATION' &&
      merchant.kycVerifiedAt === null &&
      canUse(self, 'verifyMerchantKyc')) ||
    (canUse(self, 'transitionMerchantStatus') && transitionsFrom(merchant.status).length > 0)
  );
}

/**
 * The lifecycle actions the caller may take (`MERCHANT_MANAGE`, Super and Operations Admin) for the
 * merchant's current status, in the page's sticky header (owner W4 decision). The activation
 * checklist offers the KYC check, so the header does not repeat it while the checklist lists it.
 */
export function LifecycleActions({
  compactHeadline = false,
  merchant,
  pendingOpen,
  self,
}: {
  /** Keeps the detail header on one row: secondary actions move into the overflow menu. */
  readonly compactHeadline?: boolean;
  readonly merchant: AdminMerchant;
  /** Opens a lifecycle dialog once (for example from the activation checklist). */
  readonly pendingOpen?: LifecycleOpen | null;
  readonly self: AdminSelf;
}) {
  const [open, setOpen] = useState<LifecycleOpen | null>(null);
  useEffect(() => {
    if (pendingOpen !== undefined && pendingOpen !== null) {
      setOpen(pendingOpen);
    }
  }, [pendingOpen]);
  const closed = merchant.status === 'CLOSED';
  const canEdit = !closed && canUse(self, 'updateMerchant');
  const canKyc =
    merchant.status === 'PENDING_ACTIVATION' &&
    merchant.kycVerifiedAt === null &&
    canUse(self, 'verifyMerchantKyc');
  // The checklist offers the check while it lists it; the dialog still opens from there.
  const showKyc = canKyc && !merchant.activationReadiness.missing.includes('KYC');
  const transitions = canUse(self, 'transitionMerchantStatus')
    ? transitionsFrom(merchant.status)
    : [];
  if (!canEdit && !canKyc && transitions.length === 0) {
    return null;
  }
  const close = (next: boolean) => {
    if (!next) setOpen(null);
  };
  const primaryTransitions = transitions.filter((transition) => transition.target === 'ACTIVE');
  const sensitiveTransitions = transitions.filter((transition) => transition.target !== 'ACTIVE');
  const overflowSecondary =
    compactHeadline && (canEdit || showKyc || sensitiveTransitions.length > 0);
  return (
    <>
      <div className="admin-actions">
        {!compactHeadline && canEdit ? (
          <>
            <Button
              onClick={() => setOpen({ kind: 'edit-identity' })}
              size="sm"
              variant="secondary"
            >
              ویرایش هویت
            </Button>
            <Button onClick={() => setOpen({ kind: 'edit-contact' })} size="sm" variant="secondary">
              ویرایش تماس
            </Button>
          </>
        ) : null}
        {!compactHeadline && showKyc ? (
          <Button onClick={() => setOpen({ kind: 'kyc' })} size="sm" variant="secondary">
            تأیید هویت
          </Button>
        ) : null}
        {primaryTransitions.map((transition) => {
          const blocked = !merchant.activationReadiness.ready;
          const missing = merchant.activationReadiness.missing.length;
          return (
            <Button
              disabled={blocked}
              key={transition.key}
              onClick={() => setOpen({ kind: 'transition', transition })}
              size="sm"
              variant="primary"
            >
              {blocked
                ? `${formatCount(missing)} پیش‌نیاز مانده`
                : TRANSITION_COPY[transition.key].label}
            </Button>
          );
        })}
        {!compactHeadline && sensitiveTransitions.length > 0 ? (
          <Menu
            align="end"
            label="کارهای حساس فروشگاه"
            trigger={<IconButton icon="more" label="کارهای حساس فروشگاه" variant="standard" />}
          >
            {sensitiveTransitions.map((transition) => {
              const closeBlocked =
                transition.key === 'close'
                  ? merchantCloseBlockedReason(merchant.closureReadiness)
                  : undefined;
              return (
                <MenuItem
                  destructive
                  disabled={closeBlocked !== undefined}
                  key={transition.key}
                  onSelect={() => setOpen({ kind: 'transition', transition })}
                >
                  {closeBlocked === undefined
                    ? TRANSITION_COPY[transition.key].label
                    : `${TRANSITION_COPY[transition.key].label} (${closeBlocked})`}
                </MenuItem>
              );
            })}
          </Menu>
        ) : null}
        {overflowSecondary ? (
          <Menu
            align="end"
            label="عملیات فروشگاه"
            trigger={<IconButton icon="more" label="عملیات بیشتر" type="button" variant="tonal" />}
          >
            {canEdit ? (
              <>
                <MenuItem onSelect={() => setOpen({ kind: 'edit-identity' })}>ویرایش هویت</MenuItem>
                <MenuItem onSelect={() => setOpen({ kind: 'edit-contact' })}>ویرایش تماس</MenuItem>
              </>
            ) : null}
            {showKyc ? (
              <MenuItem onSelect={() => setOpen({ kind: 'kyc' })}>تأیید هویت</MenuItem>
            ) : null}
            {sensitiveTransitions.map((transition) => {
              const closeBlocked =
                transition.key === 'close'
                  ? merchantCloseBlockedReason(merchant.closureReadiness)
                  : undefined;
              return (
                <MenuItem
                  destructive
                  disabled={closeBlocked !== undefined}
                  key={transition.key}
                  onSelect={() => setOpen({ kind: 'transition', transition })}
                >
                  {closeBlocked === undefined
                    ? TRANSITION_COPY[transition.key].label
                    : `${TRANSITION_COPY[transition.key].label} (${closeBlocked})`}
                </MenuItem>
              );
            })}
          </Menu>
        ) : null}
      </div>
      {open?.kind === 'edit-identity' ? (
        <EditIdentityDialog merchant={merchant} onOpenChange={close} />
      ) : null}
      {open?.kind === 'edit-contact' ? (
        <EditContactDialog merchant={merchant} onOpenChange={close} />
      ) : null}
      {open?.kind === 'kyc' ? <KycDialog merchant={merchant} onOpenChange={close} /> : null}
      {open?.kind === 'transition' ? (
        <TransitionDialog merchant={merchant} onOpenChange={close} transition={open.transition} />
      ) : null}
    </>
  );
}
